import { useState, useEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth, PermissionProvider, usePermissions } from '@helios/auth-firebase-web';
import { PublicAppointmentPage } from './pages/public/PublicAppointmentPage';
import type { Claims } from '@helios/authz';
import { LoginPage, getBranding } from './pages/LoginPage';
import { HomeDashboardPage } from './pages/HomeDashboardPage';
import { NoAccessScreen } from './components/NoAccessScreen';
import { NoAccess } from './components/NoAccess';
import { PowerSyncProvider } from './adapters/powersync';
import { APP_POLICY, hasValidRoleClaim } from './auth/appPolicy';
import { RouteGuard } from './auth/RouteGuard';
import { CommonShellLayout } from './shell/CommonShellLayout';
import { HomeShellLayout } from './shell/HomeShellLayout';
import { ClinicShellLayout } from './shell/ClinicShellLayout';
import { PatientShellLayout } from './shell/PatientShellLayout';
import { AppointmentsShellLayout } from './shell/AppointmentsShellLayout';
import { QueueShellLayout } from './shell/QueueShellLayout';
import { InventoryShellLayout } from './shell/InventoryShellLayout';
import { CreatePatientPage } from './pages/CreatePatientPage';
import { RegistrationFormPage } from './pages/RegistrationFormPage';
import { ProvidersPage } from './pages/appointments/ProvidersPage';
import { ProviderAvailabilityPage } from './pages/appointments/ProviderAvailabilityPage';
import { CategoryStatusesPage } from './pages/appointments/CategoryStatusesPage';
import { ServicesPage } from './pages/appointments/ServicesPage';
import { MonthViewPage } from './pages/appointments/MonthViewPage';
import { OnlineBookingSettingsPage } from './pages/appointments/OnlineBookingSettingsPage';
import { FrontDeskBookingSettingsPage } from './pages/appointments/FrontDeskBookingSettingsPage';
import { WeekViewPage } from './pages/appointments/WeekViewPage';
import { DayViewPage } from './pages/appointments/DayViewPage';
import { ListViewPage } from './pages/appointments/ListViewPage';
import { CalendarSettingsPage } from './pages/appointments/CalendarSettingsPage';
import { IntakeFormBuilderPage } from './pages/IntakeFormBuilderPage';
import { TodayPatientsPage } from './pages/patients/TodayPatientsPage';
import { FavoritePatientsPage } from './pages/patients/FavoritePatientsPage';
import { OutstandingBalancesPage } from './pages/billing/OutstandingBalancesPage';
import { AllPatientsPage } from './pages/patients/AllPatientsPage';
import { PatientProfileSettingsPage } from './pages/settings/PatientProfileSettingsPage';
import { PatientFormSettingsPage } from './pages/settings/PatientFormSettingsPage';
import { VitalsDefinitionsStubPage } from './pages/settings/VitalsDefinitionsStubPage';
import { SyncStatusPage } from './pages/settings/SyncStatusPage';
import { ProfileAccountPage } from './pages/settings/ProfileAccountPage';
import { ClinicSettingsPage } from './pages/settings/ClinicSettingsPage';
import { SystemInfoPage } from './pages/SystemInfoPage';
import { PatientConsultationPage } from './pages/PatientConsultationPage';
import { PatientAppointmentsPage } from './pages/patient/PatientAppointmentsPage';
import { PatientPharmacyPage } from './pages/patient/PatientPharmacyPage';
import { PatientPackagesPage } from './pages/patient/PatientPackagesPage';
import { PatientFilesPage } from './pages/patient/PatientFilesPage';
import { EncountersListPage } from './pages/patient/encounters/EncountersListPage';
import { EncounterDetailPage } from './pages/patient/encounters/EncounterDetailPage';
import { FormsListPage } from './pages/patients/forms/FormsListPage';
import { FormWizardPage } from './pages/patients/forms/FormWizardPage';
import { ChartingTemplatesPage } from './pages/clinic/ChartingTemplatesPage';
import { PrescriptionTemplatesPage } from './pages/patients/prescription-templates/PrescriptionTemplatesPage';
import { PatientWorkspaceSettingsPage } from './pages/clinic/PatientWorkspaceSettingsPage';
import { PrescriptionPrintWizardPage } from './pages/clinic/prescription-print/PrescriptionPrintWizardPage';
import { InvoicePrintWizardPage } from './pages/clinic/invoice-print/InvoicePrintWizardPage';
import { PurchaseOrderPrintWizardPage } from './pages/clinic/purchase-order-print/PurchaseOrderPrintWizardPage';
import { InventoryItemsPage } from './pages/inventory/InventoryItemsPage';
import { CreateInventoryItemPage } from './pages/inventory/CreateInventoryItemPage';
import { InventoryAdjustmentsPage } from './pages/inventory/InventoryAdjustmentsPage';
import { CreateInventoryAdjustmentPage } from './pages/inventory/CreateInventoryAdjustmentPage';
import { CreatePurchaseOrderPage } from './pages/inventory/CreatePurchaseOrderPage';
import { ReceiveDeliveryOrderPage } from './pages/inventory/ReceiveDeliveryOrderPage';
import { PurchaseOrdersPage } from './pages/inventory/PurchaseOrdersPage';
import { DeliveryOrdersPage } from './pages/inventory/DeliveryOrdersPage';
import { DirectDeliveryReceiptPage } from './pages/inventory/DirectDeliveryReceiptPage';
import { SuppliersPage } from './pages/inventory/SuppliersPage';
import { StockCardPage } from './pages/inventory/StockCardPage';
import { PharmacyPage } from './pages/inventory/PharmacyPage';
import { GlobalInvoicesPage } from './pages/inventory/GlobalInvoicesPage';
import { GlobalPatientPackagesPage } from './pages/inventory/GlobalPatientPackagesPage';
import { FormRendererPage } from './pages/patient/encounters/FormRendererPage';
import { PatientGroupEditPage } from './pages/patient/PatientGroupEditPage';
import { PatientTabsProvider } from './shell/PatientTabsContext';
import { EliaAiShellProvider } from './shell/elia-ai';
import { AppointmentBookedStatusProvider } from './notifications/AppointmentBookedStatus';
import {
  SyncGateProvider,
  SyncGate,
  StuckSyncModal,
  SyncReauthModal,
  StuckDownloadModal,
  AutoDownloadRecovery,
} from './sync';
import { lazy, Suspense } from 'react';
import { usePinnedRoutesInit, PinnedRoutesProvider } from './shell/usePinnedRoutes';

const QueuePage = lazy(() => import('./pages/queue/QueuePage'));

/**
 * Get PowerSync URL from environment variables.
 */
function getPowerSyncUrl(): string {
  const url = import.meta.env.VITE_POWERSYNC_URL;
  if (!url) {
    throw new Error('Missing VITE_POWERSYNC_URL in environment');
  }
  return url;
}

const MOBILE_MAX_WIDTH = 768;

function MobileBlockScreen() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        background: '#f3f4f7',
        textAlign: 'center',
        fontFamily: 'var(--font-family, system-ui, sans-serif)',
      }}
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#64748b"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12" y2="18" />
      </svg>
      <h1
        style={{
          fontSize: 20,
          fontWeight: 600,
          color: '#0f172a',
          margin: '24px 0 8px',
        }}
      >
        Desktop or Tablet Required
      </h1>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.6,
          color: '#64748b',
          maxWidth: 320,
          margin: 0,
        }}
      >
        PowerCare AI is designed for desktop and tablet screens. Please open this app on a device
        with a screen width of at least 768px.
      </p>
    </div>
  );
}

export function App() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_MAX_WIDTH);
  const { user, claims, isLoading, signOut } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Scope to `screen` so PRINT media never trips the mobile guard. Without
    // this, printing on a tablet reflows to the (narrow) print viewport, flips
    // `isMobile` true, and replaces the whole app — including the hidden
    // print-only prescription/invoice mount — with the block screen, so the
    // printout captures "Desktop or Tablet Required" instead of the document.
    const mq = window.matchMedia(`screen and (max-width: ${MOBILE_MAX_WIDTH - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // PRD.255 — public, unauthenticated appointment page. Must short-circuit
  // ABOVE the mobile block, auth gate, and PowerSync provider: it's for
  // patients on phones with no login.
  if (location.pathname.startsWith('/a/')) {
    return (
      <Routes>
        <Route path="/a/:appointmentId" element={<PublicAppointmentPage />} />
      </Routes>
    );
  }

  if (isMobile) {
    return <MobileBlockScreen />;
  }

  // Still initializing auth — wait before deciding login vs app
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  // Not signed in - show login
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Signed in but no recognizable role claim at all — deny at the identity
  // level. Users with a valid role but no accessible modules (or a permission
  // load error) are handled after permissions load, inside AuthenticatedApp.
  if (!hasValidRoleClaim(claims)) {
    return (
      <NoAccessScreen
        branding={getBranding()}
        onSignOut={signOut}
        claims={claims}
        appPolicy={APP_POLICY}
      />
    );
  }

  // Authenticated and authorized — render inner component (hooks must not be conditional)
  return <AuthenticatedApp user={user} claims={claims} />;
}

function AuthenticatedApp({ user, claims }: { user: { uid: string }; claims: Claims | null }) {
  const orgId = claims?.activeOrg ?? '';
  const roles = orgId ? (claims?.orgs?.[orgId]?.roles ?? []) : [];
  const primaryRole = roles.includes('admin')
    ? 'admin'
    : roles.includes('editor')
      ? 'editor'
      : roles[0];
  const pinnedRoutesCtx = usePinnedRoutesInit({
    orgKey: import.meta.env.VITE_CLIENT_ID || 'default',
    userId: user.uid,
    role: primaryRole,
  });

  return (
    <PermissionProvider>
      <PermissionEntryGate>
        <PowerSyncProvider powerSyncUrl={getPowerSyncUrl()}>
          <SyncGateProvider>
            <SyncGate>
              {/* PRD.253 §3 — global stuck-sync modal, surfaces on any page */}
              <StuckSyncModal />
              {/* Session-expired (JWT) modal — catches download-only staleness
              (PSYNC_S2103) where there are no pending uploads to flag. */}
              <SyncReauthModal />
              {/* Stuck re-open loop (e.g. after a sync-rules change) — offers a
              safe Force Resync when there are no pending uploads to lose. */}
              <StuckDownloadModal />
              {/* Automatic recovery for a persistent download transport failure:
              silent force-resync when there are no pending uploads (guarded,
              browser-wide-coordinated); defers to the modals above otherwise. */}
              <AutoDownloadRecovery />
              <PinnedRoutesProvider value={pinnedRoutesCtx}>
                <PatientTabsProvider>
                  {/* PRD.275 — Elia AI lives at shell level so the in-memory
                  conversation survives navigation and drawer close/reopen. */}
                  <EliaAiShellProvider>
                    {/* PRD.299 — booking-confirmation status lives above the
                    router so it outlives the booking drawer that starts it. */}
                    <AppointmentBookedStatusProvider>
                      <Routes>
                        {/* PRD.268 — all protected shells sit under RouteGuard, which
                    denies routes the user's merged permissions don't allow. */}
                        <Route element={<RouteGuard />}>
                          <Route element={<HomeShellLayout />}>
                            <Route path="/" element={<HomeDashboardPage />} />
                          </Route>
                          <Route element={<CommonShellLayout />}>
                            <Route path="/settings/clinic" element={<ClinicSettingsPage />} />
                            <Route path="/settings/profile" element={<ProfileAccountPage />} />
                            <Route path="/clinic/sync-status" element={<SyncStatusPage />} />
                            <Route path="/clinic/system-info" element={<SystemInfoPage />} />
                          </Route>
                          <Route element={<ClinicShellLayout />}>
                            <Route path="/patient/new" element={<CreatePatientPage />} />
                            <Route path="/patient/register" element={<RegistrationFormPage />} />
                            <Route path="/patients/today" element={<TodayPatientsPage />} />
                            <Route path="/patients/favorites" element={<FavoritePatientsPage />} />
                            {/* PRD.249 — Outstanding Balances follow-up page. */}
                            <Route
                              path="/clinic/outstanding"
                              element={<OutstandingBalancesPage />}
                            />
                            {/* PRD.218 — Prescription Templates builder. Must precede the
                      `/patients` index route so the static segment matches first. */}
                            <Route
                              path="/patients/prescription-templates"
                              element={<PrescriptionTemplatesPage />}
                            />
                            <Route path="/patients" element={<AllPatientsPage />} />
                            <Route path="/clinic/intake-form" element={<IntakeFormBuilderPage />} />
                            <Route
                              path="/clinic/patient-profile"
                              element={<PatientProfileSettingsPage />}
                            />
                            <Route
                              path="/clinic/patient-form"
                              element={<PatientFormSettingsPage />}
                            />
                            <Route
                              path="/clinic/vitals-definitions"
                              element={<VitalsDefinitionsStubPage />}
                            />
                            <Route path="/clinic/forms" element={<FormsListPage />} />
                            <Route path="/clinic/forms/:formId" element={<FormWizardPage />} />
                            <Route
                              path="/clinic/prescription-print"
                              element={<PrescriptionPrintWizardPage />}
                            />
                            <Route
                              path="/clinic/invoice-print"
                              element={<InvoicePrintWizardPage />}
                            />
                            {/* PRD.239 — Purchase Order Print Wizard */}
                            <Route
                              path="/clinic/purchase-order-print"
                              element={<PurchaseOrderPrintWizardPage />}
                            />
                            <Route
                              path="/clinic/charting-templates"
                              element={<ChartingTemplatesPage />}
                            />
                            <Route
                              path="/clinic/patient-workspace"
                              element={<PatientWorkspaceSettingsPage />}
                            />
                          </Route>
                          <Route element={<QueueShellLayout />}>
                            <Route
                              path="/clinic/queue"
                              element={
                                <Suspense fallback={null}>
                                  <QueuePage />
                                </Suspense>
                              }
                            />
                          </Route>
                          <Route element={<InventoryShellLayout />}>
                            <Route
                              path="/inventory"
                              element={<Navigate to="/inventory/items" replace />}
                            />
                            <Route path="/inventory/items" element={<InventoryItemsPage />} />
                            <Route
                              path="/inventory/items/new"
                              element={<CreateInventoryItemPage />}
                            />
                            {/* PRD.260 — Edit inventory item (used by packages for the
                      Included Items builder). */}
                            <Route
                              path="/inventory/items/:id/edit"
                              element={<CreateInventoryItemPage />}
                            />
                            <Route
                              path="/inventory/adjustments"
                              element={<InventoryAdjustmentsPage />}
                            />
                            <Route
                              path="/inventory/adjustments/new"
                              element={<CreateInventoryAdjustmentPage />}
                            />
                            {/* PRD.207 — Suppliers master data list/edit. */}
                            <Route path="/inventory/suppliers" element={<SuppliersPage />} />
                            <Route
                              path="/inventory/purchase-orders"
                              element={<PurchaseOrdersPage />}
                            />
                            <Route
                              path="/inventory/purchase-orders/new"
                              element={<CreatePurchaseOrderPage />}
                            />
                            <Route
                              path="/inventory/purchase-orders/:purchaseOrderId/receive"
                              element={<ReceiveDeliveryOrderPage />}
                            />
                            <Route
                              path="/inventory/delivery-orders"
                              element={<DeliveryOrdersPage />}
                            />
                            {/* PRD.265 — Direct Delivery Receipt (no Purchase Order). */}
                            <Route
                              path="/inventory/delivery-orders/direct/new"
                              element={<DirectDeliveryReceiptPage />}
                            />
                            <Route
                              path="/inventory/delivery-orders/direct/:deliveryOrderId"
                              element={<DirectDeliveryReceiptPage />}
                            />
                            <Route path="/inventory/stock-card" element={<StockCardPage />} />
                            <Route path="/inventory/pharmacy" element={<PharmacyPage />} />
                            {/* PRD.216 — Global Invoice operational module. */}
                            <Route path="/inventory/invoices" element={<GlobalInvoicesPage />} />
                            {/* PRD.263 — Global Patient Packages operations page. */}
                            <Route
                              path="/inventory/patient-packages"
                              element={<GlobalPatientPackagesPage />}
                            />
                            {/* PRD.239 — Purchase Order Print Wizard reachable
                      from the Inventory sidebar (parallel to the
                      /clinic/purchase-order-print route). */}
                            <Route
                              path="/inventory/purchase-order-print"
                              element={<PurchaseOrderPrintWizardPage />}
                            />
                          </Route>
                          <Route element={<AppointmentsShellLayout />}>
                            <Route path="/appointments/month" element={<MonthViewPage />} />
                            <Route path="/appointments/week" element={<WeekViewPage />} />
                            <Route path="/appointments/day" element={<DayViewPage />} />
                            <Route path="/appointments/list" element={<ListViewPage />} />
                            <Route path="/appointments/providers" element={<ProvidersPage />} />
                            <Route
                              path="/appointments/provider-availability"
                              element={<ProviderAvailabilityPage />}
                            />
                            <Route path="/appointments/services" element={<ServicesPage />} />
                            <Route
                              path="/appointments/settings/calendar"
                              element={<CalendarSettingsPage />}
                            />
                            <Route
                              path="/appointments/settings/categories"
                              element={<CategoryStatusesPage />}
                            />
                            <Route
                              path="/appointments/settings/online-booking"
                              element={<OnlineBookingSettingsPage />}
                            />
                            <Route
                              path="/appointments/settings/front-desk-booking"
                              element={<FrontDeskBookingSettingsPage />}
                            />
                          </Route>
                          <Route element={<PatientShellLayout />}>
                            <Route
                              path="/patient/:id"
                              element={<Navigate to="encounters" replace />}
                            />
                            <Route
                              path="/patient/:id/encounters"
                              element={<EncountersListPage />}
                            />
                            <Route
                              path="/patient/:id/encounters/:encounterId"
                              element={<EncounterDetailPage />}
                            />
                            <Route
                              path="/patient/:id/encounters/:encounterId/forms/:formInstanceId"
                              element={<FormRendererPage />}
                            />
                            <Route
                              path="/patient/:id/edit/:group"
                              element={<PatientGroupEditPage />}
                            />
                            <Route
                              path="/patient/:id/consultation"
                              element={<PatientConsultationPage />}
                            />
                            <Route
                              path="/patient/:id/appointments"
                              element={<PatientAppointmentsPage />}
                            />
                            <Route path="/patient/:id/pharmacy" element={<PatientPharmacyPage />} />
                            <Route path="/patient/:id/packages" element={<PatientPackagesPage />} />
                            <Route path="/patient/:id/files" element={<PatientFilesPage />} />
                          </Route>
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </AppointmentBookedStatusProvider>
                  </EliaAiShellProvider>
                </PatientTabsProvider>
              </PinnedRoutesProvider>
            </SyncGate>
          </SyncGateProvider>
        </PowerSyncProvider>
      </PermissionEntryGate>
    </PermissionProvider>
  );
}

/**
 * PRD.268 — permissions-derived entry gate. Holds rendering of the app shell
 * (and PowerSync) until Firestore permissions are loaded, so unauthorized
 * modules never flash. Fails closed: a load error or a valid role with no
 * accessible modules shows the No-Access / configuration screen.
 */
function PermissionEntryGate({ children }: { children: ReactNode }) {
  const { status, hasAccess, error } = usePermissions();
  const { signOut } = useAuth();

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading permissions…</div>
      </div>
    );
  }

  if (status === 'error' || !hasAccess) {
    return <NoAccess variant="config" onSignOut={signOut} detail={error ?? undefined} />;
  }

  return <>{children}</>;
}
